import React, { useMemo, useState } from 'react';
import NetworkGraphVisualization from '../NetworkGraphVisualization';
import {
    NetworkItemLike,
    itemMetadata,
    mergeGraphs,
    sortPatterns,
    toPatternSummary,
} from './networkGraphModel';
import { dsVar, ink } from '../../../theme/tokens';

interface CombinedNetworkGraphProps {
    /** The NETWORK_GRAPH items of one group, newest first (as the API returns them). */
    items: NetworkItemLike[];
    safeValue: (val: any) => React.ReactNode;
}

const SEVERITY_COLORS: Record<string, string> = {
    critical: '#EF4444',
    high: '#EF4444',
    medium: '#F59E0B',
    low: '#10B981',
};

const badgeStyle = (color: string): React.CSSProperties => ({
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: ink(color),
    border: `1px solid ${color}55`,
    background: `${color}1a`,
    borderRadius: 4,
    padding: '1px 6px',
    whiteSpace: 'nowrap',
});

/**
 * Merges a group's network visuals into one graph (an attribute shared across
 * patterns is one node) with a pattern panel: selecting a pattern highlights its
 * entities and the links between them.
 */
const CombinedNetworkGraph: React.FC<CombinedNetworkGraphProps> = ({ items, safeValue }) => {
    const [selected, setSelected] = useState<number | null>(null);

    const merged = useMemo(() => mergeGraphs(items.map(i => i.value?.data)), [items]);
    const patterns = useMemo(() => sortPatterns(items.map(toPatternSummary)), [items]);
    // The newest visual's styling: older records lack the demographic types.
    const metadata = useMemo(() => itemMetadata(items[0] || {}), [items]);
    const highlight = useMemo(
        () => (selected === null ? undefined : patterns.find(p => p.index === selected)?.entityIds),
        [selected, patterns]
    );

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, fontFamily: '"Inter", sans-serif' }}>
                <h4 className="text-[15px] font-semibold text-fg" style={{ margin: 0 }}>Combined network</h4>
                <span style={{ fontSize: 11, color: dsVar('fg-2') }}>
                    Combined from {items.length} visuals in this group · {merged.nodes.length} nodes · {merged.edges.length} links
                </span>
            </div>

            <div className="custom-scrollbar" style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', marginBottom: 14 }}>
                {patterns.map(p => {
                    const active = selected === p.index;
                    return (
                        <button
                            key={p.index}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setSelected(active ? null : p.index)}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left', cursor: 'pointer',
                                background: active ? 'rgba(255,222,89,0.08)' : dsVar('surface'),
                                border: `1px solid ${active ? dsVar('accent-fg') : dsVar('line')}`,
                                borderRadius: 8, padding: '8px 12px', fontFamily: '"Inter", sans-serif',
                            }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: active ? dsVar('accent-fg') : dsVar('fg-body') }}>{p.title}</span>
                                    {p.severity && <span style={badgeStyle(SEVERITY_COLORS[p.severity] || '#94A3B8')}>{p.severity}</span>}
                                    {p.isSameParty && <span style={badgeStyle('#f97316')}>Duplicate identity?</span>}
                                    {p.entityIds.length > 0 && (
                                        <span style={{ fontSize: 10, color: dsVar('fg-2') }}>{p.entityIds.length} entities</span>
                                    )}
                                </div>
                                {p.analysis && (
                                    <div style={{ fontSize: 11, color: dsVar('fg-2'), marginTop: 3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {p.analysis}
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            <NetworkGraphVisualization
                headers={[]}
                data={merged as any}
                metadata={metadata}
                safeValue={safeValue}
                highlightEntityIds={highlight}
            />
        </div>
    );
};

export default CombinedNetworkGraph;
